'use client';

import { useState, useTransition } from 'react';
import { useQueryStates, parseAsArrayOf, parseAsString, parseAsInteger } from 'nuqs';
import { SlidersHorizontal, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PlpFacets {
  colors: string[];
  sizes: string[];
  priceRangeCents: { min: number; max: number } | null;
}

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'featured', label: 'Destacados' },
  { value: 'newest', label: 'Más recientes' },
  { value: 'price-asc', label: 'Precio · menor a mayor' },
  { value: 'price-desc', label: 'Precio · mayor a menor' },
  { value: 'name-asc', label: 'Nombre A–Z' },
];

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function PlpFilters({ facets }: { facets: PlpFacets }) {
  const [state, setState] = useQueryStates(
    {
      colors: parseAsArrayOf(parseAsString).withDefault([]),
      sizes: parseAsArrayOf(parseAsString).withDefault([]),
      priceMax: parseAsInteger,
      sort: parseAsString.withDefault('featured'),
    },
    { shallow: false, throttleMs: 300 }
  );
  const [, startTransition] = useTransition();
  const [sheetOpen, setSheetOpen] = useState(false);

  const activeCount = state.colors.length + state.sizes.length + (state.priceMax !== null ? 1 : 0);

  function toggleArray(key: 'colors' | 'sizes', value: string) {
    const current = state[key];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    startTransition(() => void setState({ [key]: next.length > 0 ? next : null }));
  }

  function setSort(v: string) {
    startTransition(() => void setState({ sort: v === 'featured' ? null : v }));
  }

  function setPriceMax(v: number | null) {
    startTransition(() => void setState({ priceMax: v }));
  }

  function clearAll() {
    startTransition(
      () =>
        void setState({
          colors: null,
          sizes: null,
          priceMax: null,
          sort: null,
        })
    );
  }

  return (
    <>
      {/* Mobile trigger + Sort dropdown siempre visible */}
      <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3 md:hidden">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="type-nav inline-flex h-10 items-center gap-2 rounded-sm border border-[var(--color-border-strong)] px-4 text-[var(--color-fg)]"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filtros
          {activeCount > 0 ? (
            <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-accent)] px-1 text-[10px] font-bold text-[var(--color-accent-fg)]">
              {activeCount}
            </span>
          ) : null}
        </button>
        <SortSelect value={state.sort} onChange={setSort} />
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden md:sticky md:top-24 md:block md:w-[240px] md:self-start md:px-0">
        <FiltersBody
          facets={facets}
          state={state}
          onToggle={toggleArray}
          onPriceMax={setPriceMax}
          onSort={setSort}
          onClear={activeCount > 0 ? clearAll : null}
        />
      </aside>

      {/* Mobile bottom sheet */}
      {sheetOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Filtros"
        >
          <button
            type="button"
            aria-label="Cerrar"
            className="absolute inset-0 bg-[var(--color-bg)]/80"
            onClick={() => setSheetOpen(false)}
          />
          <div className="relative max-h-[85vh] w-full overflow-y-auto rounded-t-sm bg-[var(--color-surface)] p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="type-h4 text-[var(--color-fg)]">Filtros</h2>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="Cerrar filtros"
                className="text-[var(--color-fg-muted)] hover:text-[var(--color-accent)]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <FiltersBody
              facets={facets}
              state={state}
              onToggle={toggleArray}
              onPriceMax={setPriceMax}
              onSort={setSort}
              onClear={activeCount > 0 ? clearAll : null}
              showSort
            />
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="type-nav mt-6 inline-flex h-11 w-full items-center justify-center rounded-sm bg-[var(--color-accent)] px-4 text-[var(--color-accent-fg)]"
            >
              Ver resultados
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function FiltersBody({
  facets,
  state,
  onToggle,
  onPriceMax,
  onSort,
  onClear,
  showSort,
}: {
  facets: PlpFacets;
  state: { colors: string[]; sizes: string[]; priceMax: number | null; sort: string };
  onToggle: (key: 'colors' | 'sizes', value: string) => void;
  onPriceMax: (v: number | null) => void;
  onSort: (v: string) => void;
  onClear: (() => void) | null;
  showSort?: boolean;
}) {
  return (
    <div className="space-y-6">
      {showSort ? (
        <Group title="Ordenar">
          <SortSelect value={state.sort} onChange={onSort} block />
        </Group>
      ) : null}

      {facets.colors.length > 0 ? (
        <Group title="Color">
          <div className="flex flex-wrap gap-2">
            {facets.colors.map((c) => {
              const active = state.colors.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => onToggle('colors', c)}
                  className={cn(
                    'type-caption inline-flex items-center gap-1.5 rounded-xs border px-3 py-1.5 capitalize',
                    active
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
                      : 'border-[var(--color-border)] text-[var(--color-fg-muted)] hover:border-[var(--color-fg-muted)]'
                  )}
                >
                  {active ? <Check className="h-3 w-3" /> : null}
                  {c}
                </button>
              );
            })}
          </div>
        </Group>
      ) : null}

      {facets.sizes.length > 0 ? (
        <Group title="Talla">
          <div className="flex flex-wrap gap-2">
            {facets.sizes.map((s) => {
              const active = state.sizes.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => onToggle('sizes', s)}
                  className={cn(
                    'type-caption inline-flex h-8 min-w-10 items-center justify-center rounded-xs border px-2 font-mono',
                    active
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
                      : 'border-[var(--color-border)] text-[var(--color-fg-muted)] hover:border-[var(--color-fg-muted)]'
                  )}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </Group>
      ) : null}

      {facets.priceRangeCents ? (
        <Group title="Precio máximo">
          <input
            type="range"
            min={facets.priceRangeCents.min}
            max={facets.priceRangeCents.max}
            step={100}
            value={state.priceMax ?? facets.priceRangeCents.max}
            onChange={(e) => onPriceMax(Number(e.target.value))}
            className="w-full accent-[var(--color-accent)]"
            aria-label="Precio máximo"
          />
          <p className="type-caption mt-2 flex items-baseline justify-between text-[var(--color-fg-muted)]">
            <span>Hasta</span>
            <span className="text-[var(--color-fg)]">
              {formatPrice(state.priceMax ?? facets.priceRangeCents.max)}
            </span>
          </p>
          {state.priceMax !== null ? (
            <button
              type="button"
              onClick={() => onPriceMax(null)}
              className="type-caption mt-1 text-[var(--color-accent)] hover:underline"
            >
              Quitar límite
            </button>
          ) : null}
        </Group>
      ) : null}

      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          className="type-caption flex items-center gap-1 text-[var(--color-fg-muted)] hover:text-[var(--color-accent)]"
        >
          <X className="h-3 w-3" /> Limpiar filtros
        </button>
      ) : null}
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="type-eyebrow mb-3 text-[var(--color-fg-subtle)]">{title}</h3>
      {children}
    </div>
  );
}

function SortSelect({
  value,
  onChange,
  block,
}: {
  value: string;
  onChange: (v: string) => void;
  block?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Ordenar por"
      className={cn(
        'type-caption h-10 rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-[var(--color-fg)] focus:border-[var(--color-accent)] focus:outline-none',
        block ? 'w-full' : ''
      )}
    >
      {SORT_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
